import React, {useState} from 'react';
import { deviceIsMd, getEntityLink } from '../../../Services/helpers';
import Money from '../../../Components/Formatting/Money';
import DateTime from '../../../Components/Formatting/DateTime';
import BankAccount from '../../../Components/Formatting/BankAccount';
import Title from '../../../Components/Formatting/Customer/Title';
import ListViewLoading from '../../../Components/EmptyStates/ListViewLoading';
import Dropdown from '../../../Components/Dropdown';
import Icon from '../../../Components/Icons/Icon';
import Button from '../../../Components/Button/Button';
import { useDialogues } from '../../../Components/Dialogues';
import Subtitle from '../../../Components/Formatting/Customer/Subtitle';

const ListView = (props) => {
    const {transactions, hasSearchConditions, isEmpty, actioningTransaction, actionTransaction, matchCustomer, ignoreTransaction, resetTransaction, convertTransactionForCustomer} = props;
    const [confirming, setConfirming] = useState(false);

    const doConvertTransaction = async (transaction) => {
        setConfirming(true);
        await convertTransactionForCustomer(transaction, transaction.customerId);
        setConfirming(false);
    }

    if (!transactions) {
        return <ListViewLoading count={5} layout="TwoRows" />;
    }

    if (isEmpty) {
        return <p className="text-center text-muted text-sm mt-16 mx-auto max-w-[80%]">
            {hasSearchConditions ? 'No transactions match your search' : 'There are no transactions to display. If you just connected your bank account it may take a few hours for transactions to appear.'}
        </p>
    }

    return <ul className="divide-y divide-gray-200 mt-5">
        {transactions.map((transaction, i) => {
            return <TransactionRow key={transaction.internalTransactionId}
                {...{transaction, confirming, doConvertTransaction, actioningTransaction, actionTransaction, matchCustomer, ignoreTransaction, resetTransaction}}
            />;
        })}
    </ul>
}

const TransactionRow = ({transaction, confirming, doConvertTransaction, actioningTransaction, actionTransaction, matchCustomer, ignoreTransaction, resetTransaction}) => {
    const {confirm} = useDialogues();

    return <li className="py-5 first:pt-0">
        <div className="flex flex-row gap-x-6 ">
            <div className="grow flex flex-row gap-x-6">
                <div className="shrink-0">
                    <Money className="text-lg font-bold" pence={transaction.pence} /><br />
                    <TransactionStatus transaction={transaction} />
                </div>
                <div className="grow">
                    <p className="text-sm text-gray-900">
                        {transaction.description && <>{transaction.description}<br /></>}
                        <span className="text-muted">
                            {transaction.debtorName && <>{transaction.debtorName}<br /></>}
                            {transaction.debtorAccount && <><BankAccount ban={transaction.debtorAccount} /><br /></>}
                        </span>

                        <DateTime date={transaction.date} hideTime />
                    </p>

                    {(deviceIsMd() || !transaction.customerId) && <MatchedCustomer {...{transaction, doConvertTransaction, confirming}} />}
                </div>
            </div>
            <div className="shrink-0">
                <Dropdown loading={actioningTransaction === transaction.internalTransactionId}
                    options={[
                        {
                            icon: 'User',
                            title: 'View customer',
                            to: getEntityLink('Customers/Customer', transaction.customerId),
                            disabled: !transaction.customerId,
                        },
                        {
                            div: true,
                        },
                        {
                            icon: 'UserMagnifyingGlass',
                            title: 'Match to customer',
                            onClick: () => matchCustomer(transaction),
                            disabled: (transaction.newStatus ?? transaction.status) === 'Converted'
                        },
                        {
                            icon: 'EyeSlash',
                            title: 'Ignore transaction',
                            onClick: () => actionTransaction(transaction, ignoreTransaction),
                            disabled: (transaction.newStatus ?? transaction.status) === 'Converted'
                        },
                        {
                            icon: 'RotateLeft',
                            title: 'Reset',
                            onClick: () => {
                                if ((transaction.newStatus ?? transaction.status) === 'Converted') {
                                    confirm(
                                        {
                                            theme: 'warning',
                                            title: 'Reset transaction',
                                            message: "This payment record created for this transaction will be removed from the customer and deleted. Are you sure you want to reset this transaction?",
                                        },
                                        () => actionTransaction(transaction, resetTransaction)
                                    )
                                } else {
                                    actionTransaction(transaction, resetTransaction)
                                }
                            },
                            disabled: (transaction.newStatus ?? transaction.status) === 'New'
                        },
                    ]}
                />
            </div>
        </div>
        {(!deviceIsMd() && transaction.customerId) && <div className="w-full">
            <MatchedCustomer {...{transaction, doConvertTransaction, confirming}} />
        </div>}
    </li>
}

const MatchedCustomer = ({transaction, doConvertTransaction, confirming}) => {
    const {confirm} = useDialogues();

    if (!transaction.customerId) {
        return <p className="mt-2 text-red-600">
            No matching customer
        </p>
    }

    return <p className="text-sm text-gray-900 mt-2">

        {transaction.customerId && <div className="flex flex-row content-center rounded-md bg-slate-100 py-2 px-4">
            <Icon icon="User" size="2x" className="self-center mr-4" />
            <span className="inline-block align-middle self-center grow">
                <Title customerId={transaction.customerId} /><br />
                <Subtitle customerId={transaction.customerId} />
            </span>

            {(transaction.status === 'Matched' && !transaction.newStatus) && <>
                <Button size={deviceIsMd() ? 'md' : 'sm'}
                    theme="primary"
                    label="Confirm"
                    icon="Check"
                    loading={confirming}
                    onClick={() => confirm(
                        {
                            theme: 'primary',
                            title: 'Confirm transaction',
                            message: <>
                                Confirm this transaction was from <Title customerId={transaction.customerId} /> and credit their account with it?
                            </>
                        },
                        () => doConvertTransaction(transaction)
                    )}
                />
            </>}
        </div>}
    </p>
}

const TransactionStatus = ({transaction}) => {
    const getStatus = (status) => {
        if (status === 'Converted') {
            return 'Confirmed';
        }

        return status;
    }

    if (transaction.newStatus) {
        return <span className="text-sm">
            <span className="line-through text-muted mr-2">{getStatus(transaction.status)}</span><br />
            <strong>{getStatus(transaction.newStatus)}</strong>
        </span>
    }

    return <span className="text-sm">{getStatus(transaction.status)}</span>;
}

export default ListView;
